import * as pdfjs from 'pdfjs-dist';
import { RecursiveCharacterTextSplitter } from "langchain/text_splitter";
import { Document } from "@langchain/core/documents";
import { getDocument, PDFDocumentProxy } from 'pdfjs-dist';
import OpenAI from 'openai';
import { v4 as uuidv4 } from 'uuid';
import fs from 'fs';

// Before any PDF processing, set the worker path:




// Create a Node.js-friendly environment for PDF.js
// Add this before your processing functions
const NodeCanvasFactory = {
  create(width: number, height: number) {
    // Instead of using canvas, we'll use a simpler approach
    return {
      width,
      height,
      ctx: {
        // Minimal implementation of the methods we need
        drawImage: () => {},
        getImageData: () => ({ data: new Uint8ClampedArray(width * height * 4) })
      },
      toDataURL() {
        // Generate a placeholder URL instead of actual image
        return `data:image/png;base64,placeholder`;
      }
    };
  },
  reset() {},
  destroy() {}
};

// OpenAI client - still used for vision API (image descriptions)
// This should ideally be moved to a backend function too, but keeping for now
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY || "",
  dangerouslyAllowBrowser: true // TODO: Move vision API calls to backend
});

// Store extracted images
export const extractedImages: Map<string, {
  dataUrl: string,
  pageNumber: number,
  description: string
}> = new Map();

/**
 * Processes a PDF file and extracts content
 */
export async function processPdfFile(file: File): Promise<{ text: string, imageDescriptions: string[] }> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    
    // Process text
    const textContent = await extractTextFromPdf(pdf);
    
    // Process images
    const imageDescriptions = await extractAndAnalyzeImages(pdf);
    
    return { text: textContent, imageDescriptions };
  } catch (error) {
    console.error('Error processing PDF:', error);
    throw new Error(`Failed to process PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extracts text from a PDF document
 */
async function extractTextFromPdf(pdf: PDFDocumentProxy): Promise<string> {
  let text = '';
  const numPages = pdf.numPages;
  
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map(item => 'str' in item ? item.str : '')
      .join(' ');
    
    text += pageText + '\n\n';
  }
  
  return text;
}

/**
 * Extracts images from PDF and gets descriptions using Vision API
 */
async function extractAndAnalyzeImages(pdf: PDFDocumentProxy): Promise<string[]> {
  const descriptions: string[] = [];
  const numPages = pdf.numPages;
  
  for (let i = 1; i <= numPages; i++) {
    try {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 }); // Higher scale for better quality
      
      // Create canvas to render the page
      const canvas = NodeCanvasFactory.create(viewport.width, viewport.height);
      const ctx = canvas.ctx;
      if (!ctx) continue;
      
      // Render the page to canvas
      await page.render({
        canvasContext: ctx,
        viewport: viewport
      }).promise;
      
      // Look for images using the operator list
      const operatorList = await page.getOperatorList();
      const hasImages = operatorList.fnArray.some(fn => 
        fn === pdfjs.OPS.paintImageXObject || 
        fn === pdfjs.OPS.paintXObject
      );
      
      // Always process the page as a potential image - this helps catch charts/graphs 
      // that might not be detected as standard images
      const dataUrl = canvas.toDataURL();
      
      try {
        // Use GPT-4o with the correct message structure
        const response = await openai.chat.completions.create({
          model: "gpt-4o",
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: "Identify and describe all charts, graphs, figures, and visual elements in this image from a PDF. Focus on explaining what data or concepts they represent, any trends shown, and numerical values if visible. If this image contains a bar chart comparing strengths vs weaknesses across countries, be very detailed in your description."
                },
                {
                  type: "image_url",
                  image_url: {
                    url: dataUrl,
                    detail: "high" // Request high detail analysis
                  }
                }
              ]
            }
          ],
          max_tokens: 500
        });
        
        const description = response.choices[0]?.message?.content || "No description available";
        
        // Store all pages that might contain valuable visual content
        if (hasImages || 
            description.toLowerCase().includes("chart") ||
            description.toLowerCase().includes("graph") ||
            description.toLowerCase().includes("figure") ||
            description.toLowerCase().includes("diagram") ||
            description.toLowerCase().includes("table") ||
            description.toLowerCase().includes("visual")) {
          
          // Generate unique ID for this image
          const imageId = uuidv4();
          
          // Store image with metadata
          extractedImages.set(imageId, {
            dataUrl,
            pageNumber: i,
            description
          });
          
          // Add special tag in the description that can be used to display the image
          descriptions.push(`[FIGURE_ID:${imageId}] Figure from page ${i}: ${description}`);
          
          console.log(`Saved image from page ${i} with ID: ${imageId}`);
        }
      } catch (error) {
        console.error(`Error analyzing image on page ${i}:`, error);
        
        // Still save the image even if API analysis fails
        const imageId = uuidv4();
        extractedImages.set(imageId, {
          dataUrl,
          pageNumber: i,
          description: `Visual content from page ${i} (Analysis unavailable)`
        });
        descriptions.push(`[FIGURE_ID:${imageId}] Figure from page ${i} - Visual content`);
      }
    } catch (pageError) {
      console.error(`Error processing page ${i}:`, pageError);
    }
  }
  
  return descriptions;
}

/**
 * Splits documents into smaller chunks for better retrieval
 */
async function splitDocuments(documents: Document[]): Promise<Document[]> {
  const textSplitter = new RecursiveCharacterTextSplitter({
    chunkSize: 1000,
    chunkOverlap: 200,
    separators: ['\n\n', '\n', '. ', ', ', ' ', '']
  });
  
  const chunks = await textSplitter.splitDocuments(documents);
  
  console.log(`Split into ${chunks.length} chunks`);
  
  return chunks;
}

/**
 * Processes a text file and splits it into chunks
 */
export async function processTextContent(text: string, source: string = 'knowledge_base.txt'): Promise<Document[]> {
  try {
    // Create a document for the text
    const document = new Document({
      pageContent: text,
      metadata: { source }
    });
    
    // Split the document into chunks
    const textSplitter = new RecursiveCharacterTextSplitter({
      chunkSize: 1000,
      chunkOverlap: 200
    });
    
    const chunks = await textSplitter.splitDocuments([document]);
    console.log(`Split text into ${chunks.length} chunks`);
    
    return chunks;
  } catch (error) {
    console.error('Error processing text:', error);
    throw new Error(`Failed to process text: ${error}`);
  }
} 